import type { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

import axios from 'axios';
import { isAuthFailedCode, gotoLoginForClient } from '@/common/utils/auth';
import { logger } from './logger';

export declare interface FetchOptions<DataT = any> extends AxiosRequestConfig<DataT> {
  upload?: boolean,
  data?: DataT,
  json?: boolean,
  cache?: boolean,
  headers?: any,
  autoGotoLogin?: boolean,
  onUploadProgress?: (progress: any) => void,
  onUploadPercent?: (percent: number, progressEvent: any) => void,
}

export declare interface FetchResult<ResT> {
  isSuccess: boolean,
  code: number | string,
  msg?: string,
  data?: ResT,
  $response?: AxiosResponse | AxiosError | Error,
}

const DEFAULT_ERROR_CODE = 999999;

// 创建 axios 实例
const instance = axios.create({
  baseURL: import.meta.env.SSR ? import.meta.env.VITE_SERVER_BASE_URL : import.meta.env.VITE_BASE_URL,
  timeout: 30000,
});

// request 拦截器
instance.interceptors.request.use(config => {
  if (config.method === 'get') {
    config.params = config.data;
    config.data = null;
  }

  return config;
}, error => {
  // Do something with request error
  console.error(error) // for debug
  Promise.reject(error)
});

export async function $fetch<ResT = any, DataT = any>(url: string, inputOptions: FetchOptions<DataT> = {}): Promise<FetchResult<ResT>> {
  const options = { ...inputOptions };

  if (!options.method) {
    options.method = 'get';
  }

  if (!options.data) {
    options.data = {} as DataT;
  }

  if (!options.headers) {
    options.headers = {};
  }

  if (options.upload === true) {
    const fd = new FormData();

    for (const key in options.data) {
      if (Object.prototype.hasOwnProperty.call(options.data, key)) {
        const value = options.data[key];

        if (value === undefined) {
          continue;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object') {
              for (const subKey in item) {
                if (Object.prototype.hasOwnProperty.call(item, subKey)) {
                  fd.append(`${key}[${index}][${subKey}]`, item[subKey]);
                }
              }
            }
            else {
              fd.append(`${key}[]`, item);
            }
          });
        }
        else {
          fd.append(key, value as (string | Blob));
        }
      }
    }

    options.data = fd as unknown as DataT;
  }
  else if (options.method === 'post') {
    if (options.json !== false) {
      options.headers['Content-Type'] = 'application/json; charset=utf-8';
    }
    else {
      options.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
  }

  if (options.method.toLowerCase() === 'get' && options.cache !== true) {
    (options.data as { _: string })._ = String(+new Date()) + String(Math.floor(Math.random() * 9999));
  }

  if (!options.onUploadProgress && options.onUploadPercent) {
    options.onUploadProgress = (progressEvent) => {
      if (progressEvent.lengthComputable) {
        options.onUploadPercent?.(Math.floor((progressEvent.loaded / progressEvent.total) * 100), progressEvent);
      }
    }
  }

  let result: FetchResult<ResT>;

  try {
    const response = await instance(url, options);

    if (import.meta.env.DEV && import.meta.env.SSR) {
      logger(response);
    }

    result = {
      isSuccess: response?.data?.code === 200,
      code: response?.data.code,
      data: response?.data?.data,
      msg: response?.data?.msg,
      $response: response,
    }
  }
  catch (error: any) {
    if (axios.isAxiosError(error)) {
      const response = error.response;

      result = {
        isSuccess: false,
        code: response?.data?.code ?? DEFAULT_ERROR_CODE,
        data: response?.data?.data,
        msg: response?.data?.msg || error.message,
        $response: response,
      }
    }
    else {
      result = {
        isSuccess: false,
        code: DEFAULT_ERROR_CODE,
        msg: error.message,
        $response: error,
      }
    }
  }

  if (!result.isSuccess) {
    if (isAuthFailedCode(Number(result.code))) {
      if (!import.meta.env.SSR && options.autoGotoLogin === true) {
        gotoLoginForClient();
      }
    }
  }

  return result;
}
